import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Package, Users as UsersIcon, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BrandCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", deadline: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Campaign name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        deadline: form.deadline || null,
      };
      await api.post("/campaigns", payload);
      toast.success("Campaign created — influencers can now submit.");
      setForm({ name: "", description: "", deadline: "" });
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in-up" data-testid="brand-campaigns-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-black text-4xl">Your Campaigns</h1>
          <p className="text-zinc-400 mt-1">Create a campaign and let influencers submit content directly.</p>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="brand-new-campaign-button"
              className="bg-[#FFD700] hover:bg-[#FFDF33] text-black font-bold rounded-full px-5 h-11"
            >
              <Plus size={18} weight="bold" className="mr-1" /> New campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-white/10 text-zinc-50 sm:max-w-md" data-testid="brand-create-campaign-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading font-black text-2xl">New campaign</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-zinc-300">Campaign name</Label>
                <Input
                  data-testid="brand-campaign-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#111] border-white/10 mt-1.5 h-11"
                  placeholder="Summer Drop 2026"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Brief</Label>
                <Textarea
                  data-testid="brand-campaign-desc-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-[#111] border-white/10 mt-1.5 min-h-28"
                  placeholder="Tone, deliverables, do's and don'ts, hashtags, deadline notes…"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Deadline (optional)</Label>
                <Input
                  data-testid="brand-campaign-deadline-input"
                  type="date"
                  value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="bg-[#111] border-white/10 mt-1.5 h-11"
                />
              </div>
              <div className="rounded-lg border border-[#FFD700]/25 bg-[#FFD700]/5 px-3 py-2 text-xs text-[#FFE44D] font-mono">
                Your campaign becomes visible to every influencer on Mastra Media as soon as you save.
              </div>
              <Button
                onClick={create}
                disabled={saving}
                data-testid="brand-create-campaign-submit"
                className="w-full bg-[#FFD700] hover:bg-[#FFDF33] text-black font-bold h-11"
              >
                {saving ? "Saving…" : "Publish campaign"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="rounded-xl border border-white/10 bg-[#0a0a0a] p-12 text-center text-zinc-500 text-sm font-mono">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-12 text-center">
          <Package size={40} weight="duotone" className="mx-auto text-zinc-600 mb-3" />
          <p className="text-zinc-400 mb-4">No campaigns yet. Create your first one to start receiving content.</p>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="brand-empty-create-button"
            className="bg-[#FFD700] hover:bg-[#FFDF33] text-black font-bold rounded-full px-5"
          >
            <Plus size={16} weight="bold" className="mr-1" /> Create campaign
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => (
            <div
              key={c.id}
              data-testid={`brand-campaign-card-${c.id}`}
              className="rounded-xl border border-white/10 bg-[#0a0a0a] p-5 card-hover flex flex-col"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-lg truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500 font-mono truncate">
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#FFD700]/25 bg-[#FFD700]/10 text-[#FFE44D] text-[10px] font-mono uppercase tracking-wide">
                  Open
                </span>
              </div>
              {c.description && (
                <p className="text-sm text-zinc-400 line-clamp-3 mb-3">{c.description}</p>
              )}
              {c.deadline && (
                <div className="text-xs text-zinc-500 mb-3">
                  Deadline: <span className="text-zinc-300">{new Date(c.deadline).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-zinc-500 mt-auto pt-3 border-t border-white/5">
                <span>
                  <UsersIcon size={14} className="inline mr-1" />
                  {c.influencer_ids?.length || 0} invited
                </span>
                <a
                  href="/app/brand"
                  className="text-[#FFD700] hover:text-[#FFE44D] font-medium inline-flex items-center gap-1"
                  data-testid={`brand-campaign-review-link-${c.id}`}
                >
                  Review submissions <ArrowRight size={12} weight="bold" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
